import { useEffect, useState } from "react";
import { resolveAuthMode, type AuthConnectivityMode } from "@/services/auth/connectivity";

export type AuthConnectivityState = {
  readonly mode: AuthConnectivityMode;
  readonly browserOnline: boolean;
  readonly isProbing: boolean;
};

/**
 * Tracks browser online/offline for the login mode badge.
 * Offline means no Internet — not Keycloak reachability.
 */
export function useAuthConnectivity(): AuthConnectivityState {
  const [browserOnline, setBrowserOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const onOnline = () => setBrowserOnline(true);
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return {
    mode: resolveAuthMode({ browserOnline }),
    browserOnline,
    isProbing: false,
  };
}
